import {useEffect} from "react";

import {compareCoords, getCommonCoords, getFeatureCoords} from "../../lookFunctions";
import {getCurrentLayout} from "../../../../appLayout";
import {featureHasSelectedFilters, s3FilterImagePath} from "../../../../../misc/utils";
import {WIZARD_GROUP_TYPES} from "../../../../../interfaces/wizardItemInterface";
import Misc from "../../../../../misc/misc";
import {getCompressedFeature} from "../../../../../misc/backendFunctions";
import {getBvFeatures} from "../../../../../api/server-api";
import store from "../../../../../store";
import {setWizardMissingFeatures} from "../../../../../actions/wizardFunctions";
import {getFeatureGroupFeaturesQuantity} from "../../lookNavigationFunctions";
import Config, {AVAILABLE_VISUALIZER_TYPES, getCommonVisualizerType} from "../../../../../config";
import {isSameVisualizerWizard, selectedWizardGroupIsNotInCurrentStepAnymore} from "./lookWizardFunctions2";

const regexForWizardId = /\/wizard\/(.*?)$/;

/**
 * returns current wizard list for visualizer
 * @param wizardList {[wizardItemInterface]}
 * @param activePreset {string|number}
 * @returns {wizardItemInterface[]}
 */
export const getCurrentWizardList = (wizardList, activePreset) => {
  return wizardList.filter(wizardItem => !wizardIsDisabled(wizardItem, activePreset))
}

/**
 * returns current wizard step section items
 * @param wizardItem {wizardItemInterface}
 * @param activePreset {string|number}
 * @returns {boolean}
 */
export const wizardIsDisabled = (wizardItem, activePreset) => {
  if (!isSameVisualizerWizard(wizardItem)) {
    return true
  }
  return !wizardItem.disabledScenes || (typeof activePreset !== "undefined" && wizardItem.disabledScenes.includes(activePreset))
}

/**
 * checks if we need to show the wizard in the project scope and shoppingcart for the QuotingTool
 * @param wizardList {[wizardItemInterface]}
 * @param wizardIsActive {boolean}
 * @returns {boolean}
 */
export const showWizardInsteadOfQT = (wizardList, wizardIsActive) => {
  return wizardIsActive && wizardList.length
}

/**
 * returns current wizard step section items
 * @param wizardList {[wizardItemInterface]}
 * @param location {object}
 * @returns wizardActiveItem {wizardItemInterface}
 */
export const getActiveWizardItem = (wizardList, location) => {
  if (!Array.isArray(wizardList) || !location) {
    return null
  }
  const match = location.pathname.match(regexForWizardId);
  if (!Array.isArray(match) || !match[1]) {
    return null
  }
  const stringId = +match[1];
  return wizardList.find(wizItem => wizItem.id === stringId);
}


/**
 * returns layout's featureGroup
 * @param wizardGroup {wizardGroupInterface}
 * @param layout {object}
 * @returns featureGroup {featureGroupInterface}
 */
export const getFeatureGroupFromWizardGroup = (wizardGroup, layout = getCurrentLayout()) => {
  if (!wizardGroup) {
    return null
  }
  if (layout.visualizerType === AVAILABLE_VISUALIZER_TYPES.Kitchen || layout.visualizerType === AVAILABLE_VISUALIZER_TYPES.kvScene) {
    switch (wizardGroup.groupName) {
      case "Edge Profile":
        return layout.getFeatureGroupByPartId(wizardGroup.isCenterIslandGroup ? "center_island_edge" : "edge_profile")
      case "Sink":
        return layout.getFeatureGroupByPartId(wizardGroup.isCenterIslandGroup ? "center_island_sink" : "sink")
      case "Faucet":
        return layout.getFeatureGroupByPartId(wizardGroup.isCenterIslandGroup ? "center_island_faucet" : "faucet")
      default:
        break;
    }
  }
  return layout.getFeatureGroupByGroupName(wizardGroup.groupName)
}

/**
 * returns current wizard step section items
 * @param wizardList {[wizardItemInterface]}
 * @param partId {string}
 * @param activePreset {number|string} // check
 * @param firstOnly {boolean} // determines if we need to check only first step featureGroups or all
 * @returns wizardActiveItem {wizardItemInterface}
 */
export const categoryIsInTheWizard = (wizardList, partId = "", activePreset, firstOnly = true) => {
  if (!Array.isArray(wizardList) || !partId) {
    return null
  }
  return wizardList.find(wizItem => {
    if (wizardIsDisabled(wizItem, activePreset)) {
      return false
    }
    let lookWhere = firstOnly ? getCurrentGroupsFromSource("0", wizItem) : wizItem.wizardGroups;
    if (firstOnly && Array.isArray(lookWhere) && lookWhere.length && lookWhere.every(wizGroup => wizGroup.nodeType === "custom")) {
      lookWhere = lookWhere.reduce((total, wizGroup) => {
        return [...total, ...getCurrentGroupsFromSource(wizGroup.id, wizItem)]
      }, []);
    }
    return lookWhere.some(wizardGroup => {
      if (wizardGroup.nodeType !== "group") {
        return false;
      }
      const featureGroup = getFeatureGroupFromWizardGroup(wizardGroup);
      if (!featureGroup) {
        return false
      }
      return featureGroup.partId === partId
    })
  })
}

/**
 * returns current wizard step section items
 * @param wizardList {[wizardItemInterface]}
 * @param params {object}
 * @returns wizardActiveItem {wizardItemInterface}
 */
export const getMaterialsWizardItem = (wizardList, params) => {
  if (!Array.isArray(wizardList) || !params) {
    return null
  }
  return wizardList.find(wizItem => {
    if (!isSameVisualizerWizard(wizItem)) {
      return false
    }
    return (Misc.getUrlFromString(wizItem.name) === params.partName || wizItem.id === +params.partName)
  }) || getDefaultMaterialsWizardItem()
}

/**
 * returns default wizard on switch to page
 * @returns wizardActiveItem {wizardItemInterface}
 */
export const getDefaultMaterialsWizardItem = () => {
  const {quotesState, wizardState} = store.getState();
  const {wizardList, allWizardsValues, wizardActiveItem} = wizardState;
  const {activeQuoteIndex} = quotesState;
  if (!Array.isArray(wizardList)) {
    return null
  }
  const currentVisType = getCommonVisualizerType();
  const lastSelectedWizardInThisQuote = Array.isArray(allWizardsValues) ? allWizardsValues.find(wizItem => {
    return wizItem.quoteIndex === activeQuoteIndex && wizItem.isLastActiveWizard
      && isSameVisualizerWizard(wizItem, currentVisType)
  }) : null;
  if (lastSelectedWizardInThisQuote) {
    const foundWizItem = wizardList.find(wizItem => wizItem.id === lastSelectedWizardInThisQuote.wizardItemId);
    if (foundWizItem) {
      return foundWizItem
    }
  }
  if (wizardActiveItem && isSameVisualizerWizard(wizardActiveItem, currentVisType)) {
    return wizardActiveItem
  }
  return wizardList.find(wizItem => isSameVisualizerWizard(wizItem, currentVisType)) || wizardList[0]
}

let cachedSiblingFeatures = []; // {wizardId: number, groups: [{}, {}], totalFeatures: [] }

/**
 * returns current wizard step section items
 * @param source {string}
 * @param wizardActiveItem {wizardItemInterface}
 * @returns array {[wizardGroupInterface]}
 */
export const getCurrentGroupsFromSource = (source, wizardActiveItem) => {
  if (!wizardActiveItem) {
    return []
  }
  const siblingGroups = wizardActiveItem.wizardGroups.filter(wizGroup => {
    return wizardActiveItem.wizardEdges.some(wizEdge => wizEdge.source === source && wizEdge.target === wizGroup.id)
  });
  // console.log("siblingGroups", siblingGroups);
  const areSameSiblingGroups = siblingGroups.length > 1 && siblingGroups.every(siblingGroup => {
    return (siblingGroup.nodeType === WIZARD_GROUP_TYPES.group) && (siblingGroup.groupName === siblingGroups[0].groupName)
  });
  if (areSameSiblingGroups) {
    // console.log("cachedSiblingFeatures", cachedSiblingFeatures);

    if (siblingGroups.every(wizardGroup => wizardGroup.useIncluded && wizardGroup.featuresIncluded)) { // new logic
      let featuresIncluded = [];
      const alreadyCached = cachedSiblingFeatures.find(cacheObject => {
        return (cacheObject.wizardId === wizardActiveItem.id) && (cacheObject.groups.every((wGroup, wGroupIndex) => {
          return siblingGroups[wGroupIndex] && (wGroup.id === siblingGroups[wGroupIndex].id)
        }))
      })
      // console.log("alreadyCached", alreadyCached);
      if (alreadyCached) {
        featuresIncluded = alreadyCached.totalFeatures;
      } else {
        featuresIncluded = siblingGroups.reduce((total, wizardGroup) => {
          wizardGroup.featuresIncluded.forEach(included => {
            if (!total.some(exclObj => (exclObj.featureDefinitionId === included.featureDefinitionId))) {
              total.push(included)
            }
          })
          return total
        }, []);
        cachedSiblingFeatures.push({
          wizardId: wizardActiveItem.id,
          groups: siblingGroups,
          totalFeatures: featuresIncluded
        })
      }
      const newGroup = Object.assign({}, siblingGroups[0], {
        mergedGroups: siblingGroups,
        featuresExcluded: [], // not sure if we need it
        featuresIncluded
      })
      return [newGroup]
    }

    const allFeaturesExcluded = siblingGroups.reduce((total, wizardGroup) => {
      wizardGroup.featuresExcluded.forEach(excluded => {
        if (!excluded) {
          return
        }
        if (typeof excluded === "object") { // new logic for excluded
          if (!total.some(exclObj => (typeof exclObj === "object") && (exclObj.featureDefinitionId === excluded.featureDefinitionId))) {
            total.push(excluded)
          }
        } else if (!total.includes(excluded)) {
          total.push(excluded)
        }
      })
      return total
    }, []);
    const featuresExcluded = allFeaturesExcluded.filter(exclFeature => {
      return siblingGroups.every(siblingGroup => {
        if (typeof exclFeature === "object") {
          return siblingGroup.featuresExcluded.some(exclObj => {
            return (exclObj !== null)
              && (typeof exclObj === "object")
              && (exclObj.featureDefinitionId === exclFeature.featureDefinitionId)
          })
        }
        return siblingGroup.featuresExcluded.includes(exclFeature) // reaally old support
      })
    })
    const newGroup = Object.assign({}, siblingGroups[0], {
      mergedGroups: siblingGroups,
      featuresExcluded
    })
    return [newGroup]
  }
  return siblingGroups
}

export const getCorrectedWizardGroupId = (stepGroups, stepSelectedValue) => {
  if ((stepGroups.length === 1) && stepGroups[0].mergedGroups && stepSelectedValue && stepSelectedValue.wizardGroup
    && (stepSelectedValue.wizardGroup.groupName === stepGroups[0].groupName)
    && stepGroups[0].mergedGroups.some(wizG => wizG.id === stepSelectedValue.wizardGroup.id)) {
    const correctedWizG = Object.assign({}, stepGroups[0], {
      id: stepSelectedValue.wizardGroup.id
    });
    return [correctedWizG]
  }
  return stepGroups
}

/**
 * returns fixed wizard group for merged groups
 * @param wizardGroup {wizardGroupInterface}
 * @param feature {featureInterface}
 * @returns {wizardGroupInterface}
 */
export const getCorrectedWizardGroupForFeature = (wizardGroup, feature) => {
  if (wizardGroup.mergedGroups && feature) {
    const realWizardGroup = wizardGroup.mergedGroups.find(wizG => !navItemIsExcluded({part: feature}, wizG));
    if (realWizardGroup && (realWizardGroup.id !== wizardGroup.id)) {
      return Object.assign({}, wizardGroup, {
        id: realWizardGroup.id
      })
    }
  }
  return wizardGroup
}

/**
 * returns current wizard step section items
 * @param wizardActiveItem {wizardItemInterface}
 * @param wizardActiveStep {number}
 * @param wizardSelectedValues {object}
 * @param prevStepGroups {[wizardGroupInterface]}
 * @param additionalWizardValuesToCheck {object}
 * @returns {array}
 */
export const getCurrentGroups = (wizardActiveItem,
                                 wizardActiveStep,
                                 wizardSelectedValues,
                                 prevStepGroups = [],
                                 additionalWizardValuesToCheck= null) => {
  if (!wizardActiveItem || wizardActiveStep < 0) {
    return []
  }
  const stepStr = "" + wizardActiveStep;

  const toCheckValue = wizardSelectedValues[wizardActiveStep] || (additionalWizardValuesToCheck
    ? additionalWizardValuesToCheck[wizardActiveStep] : null);

  if (wizardActiveStep === 0) {
    return getCorrectedWizardGroupId(
      getCurrentGroupsFromSource(stepStr, wizardActiveItem),
      toCheckValue
    )
  }

  const selectedPrevStep = wizardSelectedValues[wizardActiveStep - 1];
  if (!selectedPrevStep || !selectedPrevStep.wizardGroup
    || (prevStepGroups.length && selectedWizardGroupIsNotInCurrentStepAnymore(selectedPrevStep, prevStepGroups))) {
    if (prevStepGroups.length && prevStepGroups[0].id) {
      if (additionalWizardValuesToCheck) { // here we check also previously selected values to make steps similar to prev choices
        let foundPreviouslySelectedGroup = null;
        if (prevStepGroups.every(wg => wg.nodeType === "custom")) {
          Object.keys(additionalWizardValuesToCheck).forEach(key => {
            if ((key >= Object.keys(wizardSelectedValues).length)
              && !additionalWizardValuesToCheck[key].partId
              && additionalWizardValuesToCheck[key].wizardGroup) {
              prevStepGroups.forEach(wg => {
                if (wg.id === additionalWizardValuesToCheck[key].wizardGroup.id) {
                  foundPreviouslySelectedGroup = wg;
                }
              });
            }
          });
        } else {
          // here we can add simple feature group, but no need for now
        }
        if (foundPreviouslySelectedGroup) {
          return getCorrectedWizardGroupId(
            getCurrentGroupsFromSource(foundPreviouslySelectedGroup.id, wizardActiveItem),
            toCheckValue
          )
        }
      }
      const suggestedStepGroups = getCurrentGroupsFromSource(prevStepGroups[0].id, wizardActiveItem);
      if ((suggestedStepGroups.length === 1 && (suggestedStepGroups[0].nodeType === "group") && suggestedStepGroups[0].mergedGroups && suggestedStepGroups[0].mergedGroups.length)
        && additionalWizardValuesToCheck && Object.keys(additionalWizardValuesToCheck).length) {
        // here we check if there are selections in the old flow that might actually help to select current product
        if (toCheckValue && toCheckValue.wizardGroup
          && (toCheckValue.wizardGroup.groupName === suggestedStepGroups[0].groupName)
          && toCheckValue.selectedFeature
          && suggestedStepGroups[0].mergedGroups.some(wizG => !navItemIsExcluded({part: toCheckValue.selectedFeature}, wizG))) {
          return [getCorrectedWizardGroupForFeature(
            suggestedStepGroups[0],
            toCheckValue.selectedFeature
          )]
        } else if (!toCheckValue || !toCheckValue.wizardGroup || (toCheckValue.wizardGroup.groupName !== suggestedStepGroups[0].groupName)) {
          // here we check if there are selections in the old flow that might actually help to select current product
          for (let key = Object.keys(wizardSelectedValues).length + 1; key < Object.keys(additionalWizardValuesToCheck).length; key++) {
            if (key && (key > Object.keys(wizardSelectedValues).length)
              && additionalWizardValuesToCheck[key]
              && additionalWizardValuesToCheck[key].wizardGroup
              && additionalWizardValuesToCheck[key].selectedFeature
              && (additionalWizardValuesToCheck[key].wizardGroup.groupName === suggestedStepGroups[0].groupName)
              && suggestedStepGroups[0].mergedGroups.some(wizG => !navItemIsExcluded({part: additionalWizardValuesToCheck[key].selectedFeature}, wizG))) {
              return [getCorrectedWizardGroupForFeature(
                suggestedStepGroups[0],
                additionalWizardValuesToCheck[key].selectedFeature
              )]
            }
          }
        }
      }
      return getCorrectedWizardGroupId(
        suggestedStepGroups,
        toCheckValue
      )
    }
    // console.log("Previous step is not selected! Check selected values --> ", wizardSelectedValues);
    return []
  }

  if (selectedPrevStep && selectedPrevStep.wizardGroup && selectedPrevStep.wizardGroup.mergedGroups && selectedPrevStep.selectedFeature) {
    const wizardGroupWithSelectedFeature = selectedPrevStep.wizardGroup.mergedGroups.find(wizGroup => {
      return !navItemIsExcluded({part: selectedPrevStep.selectedFeature}, wizGroup)
    });
    if (wizardGroupWithSelectedFeature) {
      return getCorrectedWizardGroupId(
        getCurrentGroupsFromSource(wizardGroupWithSelectedFeature.id, wizardActiveItem),
        toCheckValue
      )
    }
  }

  // const prevStepStr = "" + (wizardActiveStep - 1);
  // const prevGroups = getCurrentGroupsFromSource(prevStepStr, wizardActiveItem);
  // if (prevGroups.length > 1 && wizardActiveStep > 0) {
  //   return [selectedPrevStep.wizardGroup]
  // }

  return getCorrectedWizardGroupId(
    getCurrentGroupsFromSource(selectedPrevStep.wizardGroup.id, wizardActiveItem),
    toCheckValue
  )
}

/**
 * returns current wizard step section items
 * @param wizardActiveItem {wizardItemInterface}
 * @param wizardSelectedValues {object}
 * @param returnAllSteps {boolean}
 * @param additionalWizardValuesToCheck {object}
 * @returns {array}
 */
export const getAvailableSteps = (wizardActiveItem, wizardSelectedValues, returnAllSteps, additionalWizardValuesToCheck = null) => {
  if (!wizardActiveItem || !wizardActiveItem.wizardGroups || !wizardActiveItem.wizardEdges) {
    return []
  }

  const maxWizardSteps = 50;

  let stepsArray = [{
    stepIndex: 0,
    stepGroups: getCorrectedWizardGroupId(getCurrentGroupsFromSource("0", wizardActiveItem), wizardSelectedValues[0]),
  }];

  for (let i = 1; i <= maxWizardSteps; i++) {
    const prevStepGroups = returnAllSteps && stepsArray[i - 1] ? stepsArray[i - 1].stepGroups : [];
    const currentGroups = getCurrentGroups(wizardActiveItem, i, wizardSelectedValues, prevStepGroups, additionalWizardValuesToCheck)
    if (!currentGroups.length) {
      break;
    }
    stepsArray.push({
      stepIndex: i,
      stepGroups: currentGroups
    })
  }

  return stepsArray
}

/**
 * returns current wizard step section items
 * @param currentGroups {[wizardGroupInterface]}
 * @returns {array}
 */
export const getWizardStepCommonCoords = (currentGroups) => {
  if (!currentGroups.length) {
    return []
  }
  let commonCoords = {};
  for (let i = 0; i < currentGroups.length; i++) {
    const wizardGroup = currentGroups[i];
    const featureGroup = getFeatureGroupFromWizardGroup(wizardGroup);
    if (!featureGroup) {
      continue
    }
    const currentCoords = featureGroup.activeFeature ? getFeatureCoords(featureGroup.activeFeature) : getCommonCoords(featureGroup);
    commonCoords = compareCoords(currentCoords, currentCoords);
  }

  return commonCoords
}

/**
 * returns closest wizard group with nodeType === group
 * @param wizardGroup {wizardGroupInterface}
 * @param wizardActiveItem {wizardItemInterface}
 * @returns {array}
 */
export const findParentWizardFeatureGroup = (wizardGroup, wizardActiveItem) => {
  if (!wizardGroup || !wizardActiveItem) {
    return null
  }
  let wizardFeatureParentGroup = null;

  let maxStack = 5;
  let currentTry = 0;
  const recursiveFindParent = (checkWizardGroup) => {
    const sourceEdge = wizardActiveItem.wizardEdges.find(wizEdge => wizEdge.target === checkWizardGroup.id);
    const nextToCheckWizardGroup = wizardActiveItem.wizardGroups.find(wizGroup => wizGroup.id === sourceEdge.source);
    if (nextToCheckWizardGroup && nextToCheckWizardGroup.nodeType === "group") {
      wizardFeatureParentGroup = nextToCheckWizardGroup;
    } else if (nextToCheckWizardGroup && currentTry < maxStack) {
      currentTry++;
      recursiveFindParent(nextToCheckWizardGroup)
    }
  }
  recursiveFindParent(wizardGroup)

  return wizardFeatureParentGroup
}
/**
 * returns closest wizard group with nodeType === group
 * @param featureGroup {featureGroupInterface}
 * @param selectedFilters {array}
 * @returns {featureInterface}
 */
export const findFeatureBySelectedFilters = (featureGroup, selectedFilters) => {
  if (!featureGroup || !selectedFilters) {
    return null
  }
  if (featureGroup.activeFeature && featureHasSelectedFilters(featureGroup.activeFeature, selectedFilters)) {
    return featureGroup.activeFeature
  }
  const layout = getCurrentLayout();
  const navItems = layout.designNavigationItems({part: featureGroup}, selectedFilters);
  if (!navItems.length) {
    return null
  }
  if (navItems.length > 1 && featureGroup.activeFeature && featureGroup.activeFeature.dbFeature.sharedFeatureName.includes(".")) {
    const skuColor = "." + featureGroup.activeFeature.dbFeature.sharedFeatureName.split(".")[1];
    const sameColor = navItems.find(navItem => navItem.part.dbFeature.sharedFeatureName.includes(skuColor));
    if (sameColor) {
      return sameColor.part
    }
  }
  return navItems[0].part
}

/**
 * @param currentFilterValue {object} filterValueInterface
 * @param filterGroup {object} filterGroupInterface
 * @returns {object}
 */
export const getFilterValueThumbnailAndStyles = (currentFilterValue, filterGroup) => {

  const getFilterItemThumbnail = () => {
    const additionalImages = currentFilterValue.additionalImages;
    if (Array.isArray(additionalImages) && additionalImages.length) {
      return additionalImages[0].imageUrl
    }
    return s3FilterImagePath(currentFilterValue.icon);
  }

  let thumbnail = getFilterItemThumbnail();
  const isColor = (filterGroup.name || "").toLowerCase().includes("color");

  const styles = {
    backgroundSize: isColor  ? "cover" : "contain",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
  };
  if (thumbnail) {
    styles.backgroundImage = "url(" + thumbnail + ")";
  }
  if (!thumbnail && currentFilterValue && isColor) {
    styles.backgroundColor = currentFilterValue.value;
  }

  return {thumbnail, styles}
}

/**
 * @param navItem {navItemInterface}
 * @param wizardGroup {wizardGroupInterface}
 * @returns {boolean}
 */
export const navItemIsExcluded = (navItem, wizardGroup) => {
  const {sku, featureDefinitionID, customDisplayName} = navItem.part.dbFeature;
  const {partName} = navItem.part; // it replaces the {groupName + "-"} from the name, same logic as we have in the dashboard
  // if (customDisplayName === "Only Front Edge") {
  //   console.log("checking excluded from wizardGroup", wizardGroup);
  //   debugger;//eslint-disable-line
  // }
  const lowerCasedName = navItem.part.getLowerCasedSharedFeatureName
    ? navItem.part.getLowerCasedSharedFeatureName()
    : (navItem.part.dbFeature.sharedFeatureName || partName || '')
      .trim().toLowerCase().replace('room - ', '');

  if (wizardGroup.useIncluded && wizardGroup.featuresIncluded) {
    return !wizardGroup.featuresIncluded.some(includedFeature => {
      return (includedFeature.sharedFeatureName && (includedFeature.sharedFeatureName === lowerCasedName))
        || (includedFeature.featureDefinitionId === featureDefinitionID)
        || (!includedFeature.sharedFeatureName && (includedFeature.name === customDisplayName))
        || (!wizardGroup.dontMatchFeaturesBySku && (includedFeature.sku && sku && (includedFeature.sku === sku)))
    })
  }
  for (let excluded of wizardGroup.featuresExcluded) {
    if (excluded && (typeof excluded === "object")) {
      if (
        (excluded.sharedFeatureName && (excluded.sharedFeatureName === lowerCasedName))
        || (excluded.featureDefinitionId === featureDefinitionID)
        || (excluded.name === customDisplayName)
        || (excluded.sku && sku && (excluded.sku === sku))
      ) {
        return true
      }
      // if (excluded.name === customDisplayName || excluded.name === sharedFeatureName || (excluded.sku && sku && (excluded.sku === sku))) {
    } else if (excluded) {
      // if (excluded === sku || excluded === sharedFeatureName || excluded === partName || excluded === featureDefinitionID) {
      if (excluded === sku || excluded === partName || excluded === featureDefinitionID) {
        return true
      }
    }
  }
  return false
}

/**
 * @param navItems {[navItemInterface]}
 * @param wizardGroup {wizardGroupInterface}
 * @returns {[navItemInterface]}
 */
export const filterWizardNavItems = (navItems, wizardGroup) => {
  if (!wizardGroup.useIncluded && (!Array.isArray(wizardGroup.featuresExcluded) || !wizardGroup.featuresExcluded.length)) {
    return navItems
  }
  // console.log("navItems to filter", navItems);
  return navItems.filter(navItem => !navItemIsExcluded(navItem, wizardGroup));
}

/**
 * @param wizardSelectedValues {object} // selected wizard steps values
 * @param wizardActiveStep {number} wizardActiveStep
 * @param wizardActiveItem {wizardItemInterface} wizardItemInterface
 * @param wizardGroup {wizardGroupInterface} wizardGroupInterface
 * @param featureGroup {featureGroupInterface} featureGroupInterface
 * @param filterGroups {[filterGroupInterface]}
 * @param searchText {string}
 * @param filters {object}
 * @param shopPageOpened {boolean}
 * @param showPrevStepsColorFirst {boolean}
 * @returns {array}
 */
export const getCurrentWizardNavItems = (wizardSelectedValues,
                                         wizardActiveStep,
                                         wizardActiveItem,
                                         wizardGroup,
                                         featureGroup = null,
                                         filterGroups = [],
                                         searchText = "",
                                         filters = [],
                                         shopPageOpened = false,
                                         showPrevStepsColorFirst = false) => {

  const layout = getCurrentLayout();
  featureGroup = featureGroup || getFeatureGroupFromWizardGroup(wizardGroup);
  if (!featureGroup) {
    return []
  }

  const selectedStepsReversed = Object.keys(wizardSelectedValues).reverse() // is needed to take last selected steps first

  selectedStepsReversed.forEach(selectedStep => {
      const {selectedFilter} = wizardSelectedValues[selectedStep];
      if (!selectedFilter || +selectedStep >= wizardActiveStep) {
        return
      }
      const parentWizardGroup = findParentWizardFeatureGroup(wizardSelectedValues[selectedStep].wizardGroup, wizardActiveItem);
      if (!parentWizardGroup) {
        return null
      }
      const parentFeatureGroup = getFeatureGroupFromWizardGroup(parentWizardGroup);
      if (parentFeatureGroup && parentFeatureGroup.partId === featureGroup.partId) {
        filters.push({
          filterId: selectedFilter.id,
          filterGroupId: selectedFilter.filterGroup_id,
        })
      }
    });

  let navItems = layout.designNavigationItems(
    {part: featureGroup},
    filters,
    searchText,
    featureGroup.activeFeature,
    "all",
    filterGroups,
    {returnAllFeatures: shopPageOpened}
  );

  navItems = filterWizardNavItems(navItems, wizardGroup);

  if (featureGroup.partId === "vanity_sink") {
    const selectedPrevStep = wizardSelectedValues[wizardActiveStep - 1];
    if (selectedPrevStep  && selectedPrevStep.partId === "vanity_faucet" && selectedPrevStep.selectedFeature) {
      const faucetParent = selectedPrevStep.selectedFeature.getParent();
      if (selectedPrevStep.selectedFeature.matchesMetaForArrayOfConditions(faucetParent.getArrayOfConditions())) {
        const selectedFaucetConditions = faucetParent.getActiveFeatureConditions(selectedPrevStep.selectedFeature)
        const filteredNavItems = navItems.filter(navItem => {
          return selectedFaucetConditions.every(condition => navItem.part.matchesMetaForCondition(condition.propName, condition.propValue))
        });
        // console.log("navItems left", filteredNavItems);
        return filteredNavItems
      }
    }
  }

  if (showPrevStepsColorFirst && !wizardSelectedValues[wizardActiveStep]) {
    const allSelectedColors = selectedStepsReversed.reduce((total, selectedStep) => {
      if (wizardSelectedValues[selectedStep] && wizardSelectedValues[selectedStep].selectedFeature) {
        const colorName = wizardSelectedValues[selectedStep].selectedFeature.getColorName(filterGroups);
        if (colorName && !total.includes(colorName)) {
          total.push(colorName)
        }
      }
      return total
    }, []);
    // console.log("navItems222", navItems);
    // console.log("allSelectedColors", allSelectedColors);
    // console.log("wizardGroup", wizardGroup);
    if (allSelectedColors.length) {
      navItems = navItems.map(navItem => {
        if (navItem.colorOptions && navItem.colorOptions.length) {
          const currentColorOption = navItem.part.getColorName(filterGroups);
          if (currentColorOption !== allSelectedColors[0]) {
            let optionMatchForPrevSelectedColor = null;
            allSelectedColors.forEach(selectedColor => {
              if (!optionMatchForPrevSelectedColor && (currentColorOption !== selectedColor)) {
                optionMatchForPrevSelectedColor = navItem.colorOptions.find(option => {
                  return option.feature.getColorName(filterGroups) === selectedColor
                })
              }
            })
            if (optionMatchForPrevSelectedColor) {
              // console.log("replaced! from", navItem);
              // console.log("replaced! to", {part: optionMatchForPrevSelectedColor});
              return {part: optionMatchForPrevSelectedColor.feature}
            }
          }
        }
        return navItem
      })
    }
  }


  return navItems
}

/**
 * is used for context menu, to detect vanity wizard or shower wizard
 * @param partIdGroups {[string]}
 * @param wizardList {[wizardItemInterface]}
 * @param activePreset {string|number}
 * @returns {wizardItemInterface}
 */
export const getExistingWizardForGroups = (partIdGroups, wizardList, activePreset) => {
  return wizardList.find(wizardItem => {
    if (wizardIsDisabled(wizardItem, activePreset)) {
      return false
    }
    // const currentGroups = getCurrentGroups(wizardItem, 0, {});
    // // console.log("currentGroups", currentGroups);
    // if (!currentGroups || !currentGroups.length) {
    //   return false
    // }
    return wizardItem.wizardGroups.some(wizardGroup => {
      const featureGroup = getFeatureGroupFromWizardGroup(wizardGroup)
      return featureGroup && partIdGroups.includes(featureGroup.partId)
    })
  });
}

/**
 * is used for removing the layers from the canvas which are not selected in the wizard
 * @param wizardActiveItem {wizardItemInterface}
 * @param wizardSelectedValues {object}
 * @param layers {[]}
 * @returns partIdGroups {[string]}
 */
export const getFilteredLayersForActiveWizard = (wizardActiveItem, wizardSelectedValues, layers) => {
  if (!wizardActiveItem || !wizardActiveItem.hideItems) {
    return layers
  }
  wizardActiveItem.wizardGroups.forEach(wizardGroup => {
    const featureGroup = getFeatureGroupFromWizardGroup(wizardGroup);
    if (featureGroup && !Object.keys(wizardSelectedValues).some(stepIndex => {
      return wizardSelectedValues[stepIndex].partId === featureGroup.partId && wizardSelectedValues[stepIndex].selectedFeature
    })) {
      layers = layers.filter(layer => layer.partId !== featureGroup.partId) // if there is a step and it is not selected - remove it
    }
  })
  return layers
}

/**
 * is used for removing the feature groups to prevent them appear after the wizard is finished
 * @param wizardActiveItem {wizardItemInterface}
 * @param wizardSelectedValues {object}
 */
export const disableNotSelectedFeatureGroups = (wizardActiveItem, wizardSelectedValues) => {
  wizardActiveItem.wizardGroups.forEach(wizardGroup => {
    const featureGroup = getFeatureGroupFromWizardGroup(wizardGroup);
    if (featureGroup && !Object.keys(wizardSelectedValues).some(stepIndex => {
      return wizardSelectedValues[stepIndex].partId === featureGroup.partId && wizardSelectedValues[stepIndex].selectedFeature
    })) {
      featureGroup.disabled = true;
      if (featureGroup.layerPositions) { // we need it here to prevent adding to the cart through restore cart on item change
        Object.keys(featureGroup.layerPositions).forEach(position => {
          featureGroup.layerPositions[position] = null;
        })
      }
      if (featureGroup.disabledClick) {
        featureGroup.disabledClick()
      }
    }
  })
}

/**
 * is used to compress the data that will be passed in the job data back and force
 * @param allWizardsValues {array}
 * @param allWizardsValues {array}
 */
export const getCompressedAllWizardValues = (allWizardsValues) => {
  const allWizardsValuesToSave = allWizardsValues.map(newWizObject => {
    let wizValues = {};
    Object.keys(newWizObject.wizardSelectedValues).forEach(stepIndex => {
      wizValues[stepIndex] = Object.assign({}, newWizObject.wizardSelectedValues[stepIndex])
      if (newWizObject.wizardSelectedValues[stepIndex].wizardGroup) {
        wizValues[stepIndex].wizardGroup = Object.assign(
          {},
          newWizObject.wizardSelectedValues[stepIndex].wizardGroup,
          {featuresExcluded: [], featuresIncluded: []} // to keep clean in json
        )
        if (wizValues[stepIndex].wizardGroup.mergedGroups) {
          wizValues[stepIndex].wizardGroup.mergedGroups = wizValues[stepIndex].wizardGroup.mergedGroups.map(wizGroup => {
            return {id: wizGroup.id, groupName: wizGroup.groupName}
          });
        }
      }
      if (newWizObject.wizardSelectedValues[stepIndex].selectedFeature) {
        wizValues[stepIndex].selectedFeature = getCompressedFeature(
          null,
          newWizObject.wizardSelectedValues[stepIndex].selectedFeature,
          newWizObject.quoteIndex
        ); // to keep clean in json
      }
    })
    return Object.assign({}, newWizObject, {wizardSelectedValues: wizValues})
  });
  // console.log("allWizardsValues to save ->", allWizardsValuesToSave);
  return allWizardsValuesToSave
}

/**
 * checks if the following step is disabled
 * @param stepIndex {number}
 * @param wizardSelectedValues {object}
 * @param wizardSteps {array}
 * @returns {boolean}
 */
export const getWizardStepIsDisabled = (stepIndex, wizardSelectedValues, wizardSteps) => {
  if (stepIndex === 0) {
    return false
  }
  if (wizardSelectedValues[stepIndex - 1] && wizardSelectedValues[stepIndex - 1].wizardGroup) {
    return false // prev step is selected, current is not disabled
  }
  const prevWizardGroup = wizardSelectedValues[stepIndex - 1]
    ? wizardSelectedValues[stepIndex - 1].wizardGroup
    : wizardSteps[stepIndex - 1] && wizardSteps[stepIndex - 1].stepGroups
      ? wizardSteps[stepIndex - 1].stepGroups[0]
      : null;
  if (prevWizardGroup && prevWizardGroup.notRequired && (stepIndex === 1 || wizardSelectedValues[stepIndex - 2])) {
    // added logic for cases when not required goes after not required so you can't jump in the end of the wizard
    // you have to have prev prev step selected to jump
    return false // if prev is not required - return false, otherwise true
  }
  return true
}

export const getWizardProductGroupName = (wizardGroup) => {
  return wizardGroup.productCartTitle || wizardGroup.title || wizardGroup.groupName || ""
}

/**
 * checks if we need to request sharedFeatureNames from backend instead of the regular request
 * @param wizardGroup {wizardGroupInterface}
 * @param featureGroup {featureGroupInterface}
 * @returns {boolean}
 */
export const shouldUseNewWizardFeaturesRequestsLogic = (wizardGroup, featureGroup) => {
  if (!wizardGroup || !wizardGroup.useIncluded || !wizardGroup.featuresIncluded || !wizardGroup.featuresIncluded.length
    || !featureGroup) {
    return false
  }
  return featureGroup.counts && Object.keys(featureGroup.counts).some(layoutType => {
    // if less - we will request features with regular request cause it is faster
    // we are checking in all layouts case they might be not uploaded(need to change after feature overhaul)
    return featureGroup.counts[layoutType] > 1000
  })
}


/**
 * checks if the following step is disabled
 * @param currentNavItems {[navItemInterface]}
 * @param wizardGroup {wizardGroupInterface}
 * @param featureGroup {featureGroupInterface}
 * @returns {boolean}
 */
export const getMissingWizardFeatures = async (currentNavItems,
                                               wizardGroup,
                                               featureGroup) => {

  if (featureGroup.requestInProgress) {
    // we will request features with regular request cause it is faster
    return false
  }

  const {wizardMissingFeatures} = store.getState().wizardState;

  if (wizardMissingFeatures[wizardGroup.groupName] && !wizardMissingFeatures[wizardGroup.groupName].length) {
    // already received everything
    return false
  }

  const missingSkus = (wizardGroup.featuresIncluded || [])
    .filter(feature => {
      if (currentNavItems.some(navItem => navItem.part.getLowerCasedSharedFeatureName() === feature.sharedFeatureName)) {
        return false
      }
      // we already know missing sku's and we don't want to request the ones that we requested already
      if (wizardMissingFeatures[wizardGroup.groupName] && wizardMissingFeatures[wizardGroup.groupName].length
        && !wizardMissingFeatures[wizardGroup.groupName].includes(feature.sharedFeatureName)) {
        return false
      }
      // we already know missing sku's and we don't want to request the ones that we requested already end
      return true
    })
    .map(feature => feature.sharedFeatureName); // convert to names only array

  const devMode = Config.isDevMode();
  if (devMode) {
    console.log("current missing skus", missingSkus);
  }

  if (!missingSkus.length) { // first time we create it with requested features // small chances but let it be
    store.dispatch(setWizardMissingFeatures(wizardGroup.groupName, missingSkus));
    return
  }

  const slicedArray = missingSkus.slice(0, 100); // take first 100 features
  const stillMissingArray = missingSkus.slice(100); // from 100 to the end

  // 2) get missing features from the backend;
  if (devMode) {
    console.log("request more missingSkus", slicedArray);
    console.log("stillMissingArray", stillMissingArray);
  }
  // loaderEnable();
  featureGroup.requestInProgress = true;
  const missingSkusResponse = await getBvFeatures(null, slicedArray, true);
  featureGroup.requestInProgress = false;

  store.dispatch(setWizardMissingFeatures(wizardGroup.groupName, stillMissingArray)); // set features that left

  if (missingSkusResponse && missingSkusResponse.data) {
    if (devMode) {
      console.log("missingSkusResponse", missingSkusResponse.data);
    }
    const layout = getCurrentLayout();
    layout.loadNewFeatures(missingSkusResponse.data);
    return true
  }

  // loaderDisable();
  return false
}

/**
 * this hook is disabling feature groups for hide items wizards because they should not be added to the cart
 * @param wizardActiveItem {wizardItemInterface}
 * @param visualizersLoadedLength {number}
 * @param wizardSelectedValues {object}
 * @param updateLookCartItem {function}
 * @returns {boolean}
 */
export const useHideItemsDisabledfeatureGroups = (wizardActiveItem, visualizersLoadedLength, wizardSelectedValues, updateLookCartItem = null) => {
  useEffect(() => {
    if (wizardActiveItem && wizardActiveItem.hideItems && visualizersLoadedLength) {
      wizardActiveItem.wizardGroups.forEach(wizardGroup => {
        if (Object.keys(wizardSelectedValues).some(key => wizardSelectedValues[key].wizardGroup.groupName === wizardGroup.groupName)) {
          return // do not disable already selected in wizard groups (restoring logic)
        }
        const featureGroup = getFeatureGroupFromWizardGroup(wizardGroup);
        if (featureGroup) {
          featureGroup.disabled = true;
          if (featureGroup.layerPositions) {
            Object.keys(featureGroup.layerPositions).forEach(position => {
              featureGroup.layerPositions[position] = null;
            })
          }
          console.log("featureGroup disabled", featureGroup);
        }
      })
    }
    if (updateLookCartItem) {
      updateLookCartItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visualizersLoadedLength]);
}

export const getNextWizardStepQuantity = (currentGroups, wizardSelectedValues, wizardActiveStep, fromFeature, wizardSqftCartItems = []) => {
  let newQuantity = 1
  if (currentGroups.length === 1) {
    const featureGroup = getFeatureGroupFromWizardGroup(currentGroups[0]);
    if (wizardSelectedValues[wizardActiveStep] && Number.isInteger(wizardSelectedValues[wizardActiveStep].quantity)) {
      newQuantity = wizardSelectedValues[wizardActiveStep].quantity;
    } else if (wizardSqftCartItems.length && (fromFeature || featureGroup)) {
      const groupName = fromFeature ? fromFeature.dbFeature.groupName : (featureGroup.dbName || featureGroup.partName);
      const addedCartItems = wizardSqftCartItems.filter(cItem => cItem.cartFeature && cItem.cartFeature.dbFeature.groupName === groupName);
      // console.log("addedCartItems", addedCartItems);
      if (addedCartItems.length) {
        newQuantity = addedCartItems.reduce((total, cItem) => total + cItem.quantity, 0);
      } else {
        newQuantity = getFeatureGroupFeaturesQuantity(fromFeature, featureGroup);
      }
    } else {
      newQuantity = getFeatureGroupFeaturesQuantity(fromFeature, featureGroup);
    }
  }
  return newQuantity
}

/**
 * this returns on qkb wizard custom groups or feature groups as features
 * @param currentGroups {[wizardGroupInterface]}
 * @returns {boolean}
 */
export const displayMaterialsWizardGroupAsFeature = (currentGroups) => {
  return currentGroups.length > 1 || (currentGroups.length === 1 && currentGroups[0].nodeType === WIZARD_GROUP_TYPES.custom)
}

/**
 * this returns on qkb wizard wizardGroup from current steps
 * @param wizardItem {wizardItemInterface}
 * @param wizardValues {object}
 * @param neededWizardGroupId {string}
 * @returns {object}
 */
export const getWizardGroupFromCurrentSteps = (wizardItem, wizardValues, neededWizardGroupId) => {
  let neededStep, neededWizardGroup;
  if (!wizardItem || !wizardValues || !neededWizardGroupId) {
    return {neededStep, neededWizardGroup}
  }
  getAvailableSteps(wizardItem, wizardValues, true).forEach(step => {
    if (neededStep) {
      return
    }
    step.stepGroups.forEach(wizGroup => {
      if (wizGroup.id === neededWizardGroupId) {
        neededStep = step;
        neededWizardGroup = wizGroup;
      } else if (wizGroup.mergedGroups) {
        wizGroup.mergedGroups.forEach(mergedGroup => {
          if (mergedGroup.id === neededWizardGroupId) {
            neededStep = step;
            neededWizardGroup = mergedGroup;
          }
        })
      }
    })
  });
  return {neededStep, neededWizardGroup}
}
